"use client";

import { useEffect, useRef, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";
import { AgentWebSocketAPI } from "../api/agent-websocket-api";
import { useRealtimeAgentStore } from "@/shared/store/realtime-agent";
import { useServiceStatusStore } from "@/shared/store/service-status";
import {
  BoundConnectionRegistry,
  connectionBindingKey,
  connectionBindingsEqual,
  type AgentConnectionBinding,
} from "./connection-binding";

/**
 * Agent WebSocket 연결 관리 훅
 *
 * 책임:
 * - WebSocket 연결 생명주기 관리
 * - 연결 상태 추적
 * - 재연결 로직
 * - 동시성 안전성 보장
 */

// 싱글톤 인스턴스와 해당 에이전트/언어 바인딩
const globalConnections = new BoundConnectionRegistry<AgentWebSocketAPI>();

// 전역 초기화 플래그 (모든 훅 인스턴스가 공유)
let isGloballyInitializing = false;
let globalInitPromise: Promise<AgentWebSocketAPI | null> | null = null;
let globalInitBinding: AgentConnectionBinding | null = null;
let globalInitGeneration = 0;

export function useAgentConnection(binding?: AgentConnectionBinding | null) {

  const bindingIdentity = connectionBindingKey(binding ?? null);

  // 스토어 액션 (리렌더링 없이 사용)
  const actionsRef = useRef<{
    setConnected: (connected: boolean) => void;
    setSessionId: (sessionId: string) => void;
    setHasAttemptedConnection: (attempted: boolean) => void;
    setReconnecting: (reconnecting: boolean) => void;
    setRetryCount: (count: number) => void;
    setHasFailedPermanently: (failed: boolean) => void;
    resetRetryState: () => void;
  } | null>(null);

  if (!actionsRef.current) {
    const store = useRealtimeAgentStore.getState();
    actionsRef.current = {
      setConnected: store.setConnected,
      setSessionId: store.setSessionId,
      setHasAttemptedConnection: store.setHasAttemptedConnection,
      setReconnecting: store.setReconnecting,
      setRetryCount: store.setRetryCount,
      setHasFailedPermanently: store.setHasFailedPermanently,
      resetRetryState: store.resetRetryState,
    };
  }

  const actions = actionsRef.current;

  /**
   * 연결 초기화 (동시성 안전)
   *
   * 동시성 문제 해결:
   * 1. 전역 플래그로 중복 초기화 방지
   * 2. Promise를 캐싱하여 동시 호출 시 같은 결과 반환
   * 3. 초기화 중인 경우 기존 Promise 재사용
   */
  const initialize = useCallback(async (
    bindingOverride?: AgentConnectionBinding | null,
  ): Promise<AgentWebSocketAPI | null> => {
    // 서비스 상태 확인
    const serviceStatus = useServiceStatusStore.getState();
    if (serviceStatus.currentStatus && !serviceStatus.shouldRetry) {
      console.log("🚫 서비스 상태로 인해 연결 차단:", serviceStatus.currentStatus.type);
      return null;
    }

    const requestedBinding = bindingOverride !== undefined
      ? bindingOverride
      : binding ?? globalConnections.getBinding();
    const currentApi = globalConnections.getResource();
    const reusableApi = globalConnections.getReusable(requestedBinding);

    // 같은 에이전트/언어에 바인딩된 연결만 재사용
    if (reusableApi?.isConnected) {
      console.log("✅ 기존 연결 재사용");
      // 중요: store.reset() 후에도 isConnected 상태를 복구해야 avatar_setup이 전송됨
      actions.setConnected(true);
      actions.setSessionId(reusableApi.currentSessionId);
      return reusableApi;
    }

    // slug 또는 언어가 바뀌면 이전 세션/소켓을 먼저 폐기한다.
    if (currentApi && !connectionBindingsEqual(globalConnections.getBinding(), requestedBinding)) {
      console.log("🧹 연결 바인딩 변경 - 기존 연결 폐기");
      globalInitGeneration++;
      globalConnections.clear();
      isGloballyInitializing = false;
      globalInitPromise = null;
      globalInitBinding = null;
    }

    // 동일 바인딩 초기화 중이면 기존 Promise 반환 (중복 초기화 방지)
    if (
      isGloballyInitializing
      && globalInitPromise
      && connectionBindingsEqual(globalInitBinding, requestedBinding)
    ) {
      console.log("⏳ 초기화 진행 중... 기존 Promise 대기");
      return globalInitPromise;
    }

    // 다른 바인딩 초기화가 진행 중이면 해당 세대를 무효화한다.
    if (isGloballyInitializing) {
      globalInitGeneration++;
      globalConnections.clear();
      isGloballyInitializing = false;
      globalInitPromise = null;
    }

    // 초기화 시작
    isGloballyInitializing = true;
    globalInitBinding = requestedBinding;
    const initGeneration = ++globalInitGeneration;

    globalInitPromise = (async () => {
      let api: AgentWebSocketAPI | null = null;
      try {
        console.log("🔌 WebSocket 초기화 시작...");
        actions.setHasAttemptedConnection(true);

        // 기존 연결이 있다면 정리
        if (globalConnections.getResource()) {
          console.log("🧹 기존 연결 정리...");
          globalConnections.clear();
        }

        // 새로운 API 인스턴스 생성
        api = new AgentWebSocketAPI({
          autoReconnect: true,
          maxRetries: 5,
        });
        globalConnections.replace(api, requestedBinding);

        // 이벤트 리스너 등록
        setupEventListeners(api, actions);

        // 연결 시작
        await api.connect();

        // 바인딩 전환 중 끝난 오래된 connect 결과는 절대 복구하지 않는다.
        if (
          initGeneration !== globalInitGeneration
          || globalConnections.getResource() !== api
        ) {
          api.destroy();
          return null;
        }

        // 스토어 업데이트
        actions.setSessionId(api.currentSessionId);

        console.log("✅ WebSocket 초기화 완료");
        return api;
      } catch (error) {
        if (initGeneration === globalInitGeneration) {
          console.error("❌ WebSocket 연결 실패:", error);
          actions.setConnected(false);
          if (api && !api.manager.hasPendingReconnect) {
            globalConnections.clearIf(api);
          }
        }
        return null;
      } finally {
        if (initGeneration === globalInitGeneration) {
          isGloballyInitializing = false;
          globalInitPromise = null;
          globalInitBinding = null;
        }
      }
    })();

    return globalInitPromise;
  }, [actions, bindingIdentity]);

  /** 바인딩 변경 시 초기화하며 StrictMode 중복 호출은 전역 Promise로 합친다. */
  useEffect(() => {
    if (!binding) return;
    console.log("🚀 useAgentConnection 초기화 시도...");
    initialize(binding).catch((error) => {
      console.error("❌ 초기화 중 에러:", error);
    });

    // Cleanup: 전역 싱글톤이므로 명시적으로 disconnect하지 않음
    // 필요시 사용자가 직접 disconnect 호출
    return () => {
      console.log("🧹 useAgentConnection cleanup (연결 유지)");
    };
  }, [bindingIdentity, initialize]);

  /**
   * 연결 종료 (동시성 안전)
   */
  const disconnect = useCallback(() => {
    console.log("🔌 연결 종료 시작...");

    // 초기화 중이면 취소
    globalInitGeneration++;
    isGloballyInitializing = false;
    globalInitPromise = null;
    globalInitBinding = null;

    globalConnections.clear();
    actions.setConnected(false);
    actions.setSessionId("");
    console.log("✅ 연결 종료 완료");
  }, [actions]);

  /**
   * 재연결
   */
  const reconnect = useCallback(async () => {
    const reconnectBinding = binding ?? globalConnections.getBinding();
    if (!reconnectBinding) throw new Error("WebSocket reconnect binding is unavailable");
    disconnect();
    const api = await initialize(reconnectBinding);
    if (!api) throw new Error("WebSocket reconnect failed");
    return api;
  }, [binding, disconnect, initialize]);

  /**
   * 수동 재시도
   */
  const manualRetry = useCallback(async () => {
    actions.resetRetryState();
    globalConnections.getResource()?.resetRetryCount();
    const reconnectBinding = binding ?? globalConnections.getBinding();
    if (!reconnectBinding) throw new Error("WebSocket reconnect binding is unavailable");
    disconnect();
    const api = await initialize(reconnectBinding);
    if (!api) throw new Error("WebSocket reconnect failed");
    return api;
  }, [actions, binding, disconnect, initialize]);

  /**
   * 연결 보장
   */
  const ensureConnection = useCallback(async (): Promise<AgentWebSocketAPI | null> => {
    const requestedBinding = binding ?? globalConnections.getBinding();
    const reusableApi = globalConnections.getReusable(requestedBinding);
    if (reusableApi?.isConnected) {
      return reusableApi;
    }
    return initialize();
  }, [bindingIdentity, initialize]);

  return {
    api: globalConnections.getResource(),
    disconnect,
    reconnect,
    manualRetry,
    ensureConnection,
  };
}

/**
 * 이벤트 리스너 설정 (순수 함수)
 */
function setupEventListeners(
  api: AgentWebSocketAPI,
  actions: {
    setConnected: (connected: boolean) => void;
    setSessionId: (sessionId: string) => void;
    setReconnecting: (reconnecting: boolean) => void;
    setRetryCount: (count: number) => void;
    setHasFailedPermanently: (failed: boolean) => void;
  }
) {
  // 연결 상태 변화
  api.manager.on("state_change", (state: string) => {
    console.log("🔌 연결 상태 변화:", state);
    const isConnected = state === "connected";
    actions.setConnected(isConnected);
    if (isConnected) {
      actions.setSessionId(api.currentSessionId);
      actions.setReconnecting(false);
      actions.setRetryCount(0);
      actions.setHasFailedPermanently(false);
    }
  });

  // 재시도 이벤트
  api.manager.on("retry_attempt", (data: { retryCount: number; maxRetries: number }) => {
    console.log(`🔄 재연결 시도: ${data.retryCount}/${data.maxRetries}`);
    actions.setReconnecting(true);
    actions.setRetryCount(data.retryCount);
  });

  // 최대 재시도 초과
  api.manager.on("max_retries_exceeded", () => {
    console.error("❌ 최대 재시도 횟수 초과");
    actions.setHasFailedPermanently(true);
    actions.setReconnecting(false);
  });

  // 연결 종료
  api.manager.on("close", () => {
    console.log("🔌 연결 종료");
    actions.setConnected(false);
  });
}

/**
 * 연결 상태만 필요한 경우
 */
export function useAgentConnectionStatus() {
  return useRealtimeAgentStore((state) => state.isConnected);
}

/**
 * 재시도 상태만 필요한 경우
 */
export function useAgentRetryStatus() {
  return useRealtimeAgentStore(
    useShallow((state) => ({
      isReconnecting: state.isReconnecting,
      retryCount: state.retryCount,
      maxRetries: state.maxRetries,
      hasFailedPermanently: state.hasFailedPermanently,
    }))
  );
}

/**
 * 세션 정보만 필요한 경우
 */
export function useAgentSessionInfo() {
  return useRealtimeAgentStore(
    useShallow((state) => ({
      sessionId: state.sessionId,
      isConnected: state.isConnected,
    }))
  );
}

/**
 * 전역 API 접근 유틸리티
 */
export const AgentConnectionManager = {
  getAPI: () => globalConnections.getResource(),
  getBinding: () => globalConnections.getBinding(),
  isConnected: () => globalConnections.getResource()?.isConnected ?? false,
  isInitializing: () => isGloballyInitializing,
  reset: () => {
    // 테스트나 디버깅용 - 모든 상태 초기화
    globalInitGeneration++;
    isGloballyInitializing = false;
    globalInitPromise = null;
    globalInitBinding = null;
    globalConnections.clear();
  },
};
